import {
  createContext,
  forwardRef,
  useContext,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import { cn } from '@helpers/cn';
import { labelMobileTable } from './helpers/labelMobileTable';

const TableHeadContext = createContext(false);

export const TableWrapper = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('relative min-w-0 w-full max-w-full overflow-x-auto', className)} {...props} />
  ),
);
TableWrapper.displayName = 'TableWrapper';

export const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(({ className, ...props }, ref) => {
  const tableRef = useRef<HTMLTableElement>(null);
  useImperativeHandle(ref, () => tableRef.current!, []);
  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) {
      return;
    }
    labelMobileTable(table);
    const observer = new MutationObserver(() => labelMobileTable(table));
    observer.observe(table, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, []);
  return <table ref={tableRef} role="table" className={cn('core-table', className)} {...props} />;
});
Table.displayName = 'Table';

type TTheadProps = HTMLAttributes<HTMLTableSectionElement> & {
  $sticky?: boolean;
  $white?: boolean;
};

export const Thead = forwardRef<HTMLTableSectionElement, TTheadProps>(
  ({ className, $sticky, $white, ...props }, ref) => (
    <TableHeadContext.Provider value={true}>
      <thead
        ref={ref}
        role="rowgroup"
        className={cn(
          'core-table-thead relative',
          $sticky ? 'sticky top-0 z-[1]' : '',
          $white ? 'bg-[var(--color-surface)]' : 'bg-[var(--color-page)]',
          "after:pointer-events-none after:absolute after:bottom-0 after:left-0 after:block after:w-full after:border-b after:border-[var(--color-border)] after:content-['']",
          className,
        )}
        {...props}
      />
    </TableHeadContext.Provider>
  ),
);
Thead.displayName = 'Thead';

export const Tr = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  ({ className, children, ...props }, ref) => {
    const head = useContext(TableHeadContext);
    const [expanded, setExpanded] = useState(false);
    return (
      <tr ref={ref} role="row" className={cn('core-table-row', className)} data-mobile-expanded={expanded} {...props}>
        {children}
        {!head && (
          <td role="cell" data-mobile-toggle className="core-table-toggle">
            <button
              type="button"
              aria-expanded={expanded}
              onClick={(event) => {
                event.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              {expanded ? 'Hide details' : 'Show details'}
            </button>
          </td>
        )}
      </tr>
    );
  },
);
Tr.displayName = 'Tr';

export const Td = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td role="cell" ref={ref} className={cn('core-table-cell', className)} {...props} />
  ),
);
Td.displayName = 'Td';

export const Th = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, onClick, onKeyDown, tabIndex, ...props }, ref) => (
    <th
      role="columnheader"
      ref={ref}
      className={cn('core-table-cell', className)}
      onClick={onClick}
      tabIndex={tabIndex ?? (onClick ? 0 : undefined)}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (
          event.target === event.currentTarget &&
          onClick &&
          !event.defaultPrevented &&
          (event.key === 'Enter' || event.key === ' ')
        ) {
          event.preventDefault();
          event.currentTarget.click();
        }
      }}
      {...props}
    />
  ),
);
Th.displayName = 'Th';

export const Tbody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <tbody role="rowgroup" ref={ref} className={className} {...props} />,
);
Tbody.displayName = 'Tbody';
